import { FilterParams } from '../../utilities/http-params'
import {
  ActiveFilterDisplay,
  FilterMeta,
  QueryParam,
} from './common'
import { FilterAction } from './filter.service'
import { ServicePointGroupWithRefs } from 'src/app/public/service-point-groups/service-point-group.types'

export const numberDisplay = (n: unknown, precision = 8) =>
  typeof n == 'number' ? n.toFixed(precision) : '-'

export type NumberRangeFilterState = Readonly<{
  min: number,
  max: number,
  config: {
    meta: FilterMeta
    allowRange: boolean
    precision: number
    constant?: boolean
  }
}>

export const initialNumberRangeFilterState: NumberRangeFilterState = {
  min: NaN,
  max: NaN,
  config: {
    meta: {
      fieldName: '',
      fieldGroup: '',
      fieldDisplay: '',
    },
    allowRange: true,
    precision: 8,
  },
}

export interface SetNumberRangeFilterConfigAction {
  type: 'SET_NUMBER_RANGE_FILTER_CONFIG_ACTION',
  payload: Pick<NumberRangeFilterState, 'config'>,
  meta: Omit<NumberRangeFilterState['config']['meta'], 'fieldDisplay'>
}

export interface SetNumberRangeFilterValueAction {
  type: 'SET_NUMBER_RANGE_FILTER_VALUE_ACTION',
  payload: Partial<Pick<NumberRangeFilterState, 'min' | 'max'>>,
  meta: Omit<NumberRangeFilterState['config']['meta'], 'fieldDisplay'>
}

export type NumberRangeFilterAction =
    | SetNumberRangeFilterConfigAction
    | SetNumberRangeFilterValueAction

export const numberRangeFilterReducer = (state: NumberRangeFilterState, action: NumberRangeFilterAction) => {
  switch (action.type) {
    case 'SET_NUMBER_RANGE_FILTER_CONFIG_ACTION':
      return {
        ...state,
        config: action.payload.config,
      }
    case 'SET_NUMBER_RANGE_FILTER_VALUE_ACTION':
      return {
        ...state,
        min: action.payload.min ?? NaN,
        max: action.payload.max ?? NaN,
      }
    default:
      return state
  }
}

export class NumberRangeFilter {
  readonly type = 'NUMBER_RANGE'

  static SetNumberRangeFilterValueAction(
    payload: SetNumberRangeFilterValueAction['payload'],
    meta: Omit<NumberRangeFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetNumberRangeFilterValueAction { return { type: 'SET_NUMBER_RANGE_FILTER_VALUE_ACTION', payload, meta } }

  state: NumberRangeFilterState

  constructor(
    partialStateWithConfig: Partial<NumberRangeFilterState> & Pick<NumberRangeFilterState, 'config'>,
    private dispatch: (action: FilterAction) => void,
  ) {
    this.state = { ...initialNumberRangeFilterState, ...partialStateWithConfig }
  }

  apply(action: NumberRangeFilterAction) {
    this.state = numberRangeFilterReducer(this.state, action)
    return this
  }

  asParams(): FilterParams[] {
    if([this.state.min, this.state.max].every(Number.isNaN)) {
      return []
    }
        
    return [
      {
        name: this.state.config.meta.fieldName,
        values: [
          !Number.isNaN(this.state.min) && !Number.isNaN(this.state.max) 
            ? ({ operation: 'between=', value: `${numberDisplay(this.state.min, this.state.config.precision)},${numberDisplay(this.state.max, this.state.config.precision)}` })
            : Number.isNaN(this.state.min) 
              ? ({ operation: '<=', value: numberDisplay(this.state.max, this.state.config.precision) })
              : ({ operation: '>=', value: numberDisplay(this.state.min, this.state.config.precision) }),
        ],
      },
    ]
  }

  public asActiveDisplay(): ActiveFilterDisplay[] {
    if([this.state.min, this.state.max].every(Number.isNaN)) {
      return []
    }

    if(!Number.isNaN(this.state.min) && !Number.isNaN(this.state.max)) {
      return [
        {
          headerName: this.state.config.meta.fieldDisplay,
          constant: this.state.config.constant ?? false,
          remove: () => this.dispatch(NumberRangeFilter.SetNumberRangeFilterValueAction(
            { min: NaN, max: NaN },
            this.state.config.meta,
          )),
          operation: 'Between', 
          value: `${numberDisplay(this.state.min, this.state.config.precision)} & ${numberDisplay(this.state.max, this.state.config.precision)}`,
        },
      ]
    }

    if(!Number.isNaN(this.state.min) && this.state.config.allowRange) {
      return [
        {
          headerName: this.state.config.meta.fieldDisplay,
          constant: this.state.config.constant ?? false,
          remove: () => this.dispatch(NumberRangeFilter.SetNumberRangeFilterValueAction(
            { min: NaN, max: NaN },
            this.state.config.meta,
          )),
          operation: '≥', 
          value: numberDisplay(this.state.min, this.state.config.precision),
        },
      ]
    }

    if(!Number.isNaN(this.state.min) && !this.state.config.allowRange) {
      return [
        {
          headerName: this.state.config.meta.fieldDisplay,
          constant: this.state.config.constant ?? false,
          remove: () => this.dispatch(NumberRangeFilter.SetNumberRangeFilterValueAction(
            { min: NaN, max: NaN },
            this.state.config.meta,
          )),
          operation: '=', 
          value: numberDisplay(this.state.min, this.state.config.precision),
        },
      ]
    }
        
    if(!Number.isNaN(this.state.max)) {
      return [
        {
          headerName: this.state.config.meta.fieldDisplay,
          constant: this.state.config.constant ?? false,
          remove: () => this.dispatch(NumberRangeFilter.SetNumberRangeFilterValueAction(
            { min: NaN, max: NaN },
            this.state.config.meta,
          )),
          operation: '≤', 
          value: numberDisplay(this.state.max, this.state.config.precision),
        },
      ]
    }

    return []
  }

  public asCount() {
    return !Number.isNaN(this.state.min) && !Number.isNaN(this.state.max) ? 1:  0
  }

  public fromUrlQueryParams(
    params: readonly QueryParam[],
    defaultVal: { min: number, max: number } | null | undefined,
  ) {
    const qp = params.find((p) => p.name === this.state.config.meta.fieldName)
    if (qp?.value) {
      const [min, max] = qp.value.split('~').map(Number.parseFloat)
      this.dispatch(NumberRangeFilter.SetNumberRangeFilterValueAction(
        { min: min ?? NaN, max: this.state.config.allowRange ? (max ?? NaN): NaN },
        this.state.config.meta,
      ))
    }
    else if (defaultVal) {
      this.dispatch(NumberRangeFilter.SetNumberRangeFilterValueAction(
        defaultVal,
        this.state.config.meta,
      ))
    }
  }

  asUrlQueryParams(): QueryParam[] {
    if([this.state.min, this.state.max].every(Number.isNaN)) {
      return []
    }
        
    return [
      {
        name: this.state.config.meta.fieldName,
        value: [this.state.min, this.state.max]
          .map(n => Number.isNaN(n) ? '' : numberDisplay(n, this.state.config.precision))
          .join('~'),
      },
    ]
  }

  asDashboardQueryParams(): QueryParam[] {
    if([this.state.min, this.state.max].every(Number.isNaN)) {
      return []
    }

    if(!Number.isNaN(this.state.min) && !Number.isNaN(this.state.max)) {
      return [{
        name: this.state.config.meta.fieldName,
        value:`${numberDisplay(this.state.min, this.state.config.precision)}~${numberDisplay(this.state.max, this.state.config.precision)}`,
      }]
    }

    if(!Number.isNaN(this.state.min) && this.state.config.allowRange) {
      return [{
        name: this.state.config.meta.fieldName,
        value:`${numberDisplay(this.state.min, this.state.config.precision)}~`,
      }]
    }

    if(!Number.isNaN(this.state.min) && !this.state.config.allowRange) {
      return [{
        name: this.state.config.meta.fieldName,
        value:`${numberDisplay(this.state.min, this.state.config.precision)}`,
      }]
    }

    if(!Number.isNaN(this.state.max)) {
      return [{
        name: this.state.config.meta.fieldName,
        value:`~${numberDisplay(this.state.max, this.state.config.precision)}`,
      }]
    }
        
    return []
  }

  asServicePointGroupWithRefs(): Partial<ServicePointGroupWithRefs> {
    return {}
  }
}
